import React from "react";
import { identity, startCase } from "lodash";
import { PanelPropertiesCard } from "@sonder/ui/source/styled/components/PanelPropertiesCard";
import { TaskData, TaskUnit } from "core/queries/GetTask";
import { BedSize } from "typings/globalGQLTypes";

type BedSizes = {
  [key in BedSize]: number;
};

// Input: [ { id: 1, bedSize: "california" }, ...]
// Output: 10 | 3 California, 2 King, 2 Queen, 1 Double, 2 Single
const convertBedSizesToString = (beds: TaskUnit["beds"]) => {
  const initialBedSizes = Object.keys(BedSize).reduce(
    (acc, memo) => ({ ...acc, [memo]: 0 }),
    {},
  ) as BedSizes;
  const bedSizes = (beds || []).reduce((acc, memo) => {
    const bedKey = memo.bedSize;
    const bedCount = acc[bedKey];
    return {
      ...acc,
      [bedKey]: bedCount + 1,
    };
  }, initialBedSizes);
  // Note, this is purely used to order the BedCounts from Largest to Smallest BedSize in the resulting string.
  // If a new BedSize is added, we would have to manually add it to the order below.
  const bedOrder = [
    BedSize.california,
    BedSize.king,
    BedSize.queen,
    BedSize.double,
    BedSize.single,
  ];
  const bedTotal = Object.values(bedSizes).reduce((acc, memo) => acc + memo, 0);
  const bedCounts = bedOrder
    .map(bedSize => {
      const bedCount = bedSizes[bedSize];
      return bedCount > 0 ? `${bedCount} ${startCase(bedSize)}` : null;
    })
    .filter(x => !!x)
    .join(", ");
  return bedTotal ? `${bedTotal} | ${bedCounts}` : null;
};

export const METADATA_INFO = {
  heading: "Unit Information",
  options: [
    {
      label: "Internal Ref",
      path: "unit.internalRef",
      transform: startCase,
    },
    {
      label: "Address",
      path: "unit.address",
      transform: startCase,
    },
    {
      label: "Bed Count",
      path: "unit.beds",
      transform: (value: string, data: TaskData) =>
        convertBedSizesToString(data.unit?.beds || []),
    },
    {
      label: "Bathroom Count",
      path: "unit.nbrBathrooms",
      transform: identity,
    },
    {
      label: "Size of Unit",
      path: "unit.squareFootage",
      transform: (value: string) => `${value} sqft`,
    },
  ],
};

export const MetadataCard = ({ task }: { task: TaskData }) => (
  <PanelPropertiesCard
    data={task}
    emptyNode="No unit associated with this task."
    heading={METADATA_INFO.heading}
    options={METADATA_INFO.options}
  />
);
